import { EventSeverity } from "@sitescene/types";

/** Default Alert Priority before the user changes filters or any values are persisted. */
export const DEFAULT_SEVERITY_FILTER = EventSeverity.Moderate;

/** URL value for "Any Priority" (`severityFilter === null`). */
export const SEVERITY_URL_PARAM_ALL = "all";

const isEventSeverity = (value: string): value is EventSeverity =>
	Object.values(EventSeverity).includes(value as EventSeverity);

/** True when Alert Priority differs from the dashboard default (Moderate). */
export const isSeverityFilterActive = (
	severityFilter: EventSeverity | null,
): boolean => severityFilter !== DEFAULT_SEVERITY_FILTER;

/**
 * Parses the `severity` query param into context state.
 * - `all` or missing when read via caller → `null` (Any Priority)
 * - Valid enum → that priority
 * - Invalid value → Moderate (default)
 */
export const parseSeverityFromUrl = (
	value: null | string,
): EventSeverity | null => {
	if (value === null || value === SEVERITY_URL_PARAM_ALL) {
		return null;
	}
	if (isEventSeverity(value)) {
		return value;
	}
	return DEFAULT_SEVERITY_FILTER;
};

/** Writes Alert Priority to URL search params, omitting the default (Moderate). */
export const applySeverityToSearchParams = (
	params: URLSearchParams,
	paramKey: string,
	severityFilter: EventSeverity | null,
): void => {
	if (severityFilter === null) {
		params.set(paramKey, SEVERITY_URL_PARAM_ALL);
	} else if (severityFilter !== DEFAULT_SEVERITY_FILTER) {
		params.set(paramKey, severityFilter);
	} else {
		params.delete(paramKey);
	}
};

/** Severity fields on the org-scoped localStorage filter blob. */
export type StoredSeverityFields = {
	severity: EventSeverity | null;
	severityExplicit?: boolean;
};

/**
 * Maps stored severity to context state.
 * Legacy blobs without `severityExplicit` treat null as Moderate, not Any Priority.
 */
export const resolveStoredSeverityFilter = (
	stored: StoredSeverityFields,
): EventSeverity | null => {
	if (stored.severityExplicit) {
		return stored.severity;
	}
	return stored.severity ?? DEFAULT_SEVERITY_FILTER;
};
